import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import { Title } from 'react-admin'
import {
  GemeenteResponse,
  OverigeDataMetaResponse,
  OverigeDataResponse,
} from '../../types/apiRequests'
import { getJson, getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'
import OverigeDataImportDialog from './OverigeDataImportDialog'
import OverigeDataTreeview from './OverigeDataTreeview'
import GemeenteAutocomplete from '../../components/inputs/GemeenteAutocomplete'
import { rememberGemeente, useLastGemeente } from '../../utils/useLastGemeente'

type Mode = 'stad' | 'gebied'
type SortOrder = 'asc' | 'desc'

interface Props {
  mode: Mode
}

const ONDERDELEN_WITH_TREEVIEW = new Set(['gv', 'wbp_gebied'])

const OverigeDataView: FC<Props> = ({ mode }) => {
  const title = mode === 'gebied' ? 'Overige data — Gebied' : 'Overige data — Stad'

  const [selectedGemeente, setSelectedGemeente] = useState<GemeenteResponse | null>(null)
  const [beheer, setBeheer] = useState(false)
  const [meta, setMeta] = useState<OverigeDataMetaResponse | null>(null)
  const [onderdeel, setOnderdeel] = useState('')
  const [code, setCode] = useState('')
  const [subSelect, setSubSelect] = useState('')
  const [gebiedCode, setGebiedCode] = useState('')
  const [tableData, setTableData] = useState<OverigeDataResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [metaLoading, setMetaLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [errorMsg, setErrorMsg] = useState('')

  const fetchData = useCallback(
    async (
      gemeenteSid: number,
      ond: string,
      c: string,
      extraSub?: string,
      extraGebied?: string,
    ) => {
      if (!ond || !c) return
      setLoading(true)
      setErrorMsg('')
      try {
        const params = new URLSearchParams()
        if (extraSub) params.append('sub_select', extraSub)
        if (extraGebied) params.append('gebied_code', extraGebied)
        const qs = params.toString() ? `?${params.toString()}` : ''
        const result = await getJson<OverigeDataResponse>(
          `${getUrl()}/gemeenten/${gemeenteSid}/overige_data/${mode}/${ond}/${encodeURIComponent(c)}${qs}`,
        )
        setTableData(result)
        setPage(0)
        if (result.error) setErrorMsg(result.error)
        if (result.sub_select && !extraSub) {
          setSubSelect(result.sub_select.selected || '')
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Onbekende fout')
        setTableData(null)
      } finally {
        setLoading(false)
      }
    },
    [mode],
  )

  const fetchMeta = useCallback(
    async (gemeenteSid: number, isBeheer: boolean) => {
      setMetaLoading(true)
      setErrorMsg('')
      try {
        const params = isBeheer ? '?beheer=true' : ''
        const result = await getJson<OverigeDataMetaResponse>(
          `${getUrl()}/gemeenten/${gemeenteSid}/overige_data/${mode}${params}`,
        )
        setMeta(result)
        const keys = Object.keys(result.onderdelen)
        if (keys.length > 0) {
          const firstOnd = keys[0]
          setOnderdeel(firstOnd)
          const codes = result.onderdelen[firstOnd].codes
          if (codes.length > 0) {
            const firstCode = codes[0].code
            setCode(firstCode)
            setSubSelect('')
            setGebiedCode('')
            void fetchData(gemeenteSid, firstOnd, firstCode)
          }
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Onbekende fout')
        setMeta(null)
      } finally {
        setMetaLoading(false)
      }
    },
    [fetchData, mode],
  )

  const resetSelections = () => {
    setMeta(null)
    setOnderdeel('')
    setCode('')
    setSubSelect('')
    setGebiedCode('')
    setTableData(null)
  }

  const handleGemeenteChange = useCallback(
    async (gem: GemeenteResponse | null) => {
      setSelectedGemeente(gem)
      rememberGemeente(gem)
      resetSelections()
      if (gem) await fetchMeta(gem.sid, beheer)
    },
    [beheer, fetchMeta],
  )

  useLastGemeente(selectedGemeente, (gem) => {
    void handleGemeenteChange(gem)
  })

  // When switching between Stad/Gebied modes (i.e. the component remounts via
  // a different wrapper), reset the beheer toggle so we never leak gebied-only
  // state into stad-mode.
  useEffect(() => {
    setBeheer(false)
  }, [mode])

  const handleBeheerChange = async (next: boolean) => {
    setBeheer(next)
    resetSelections()
    if (selectedGemeente) await fetchMeta(selectedGemeente.sid, next)
  }

  const handleOnderdeelChange = (ond: string) => {
    setOnderdeel(ond)
    setSubSelect('')
    setGebiedCode('')
    setCode('')
    setTableData(null)
    if (meta?.onderdelen[ond]?.codes.length) {
      const firstCode = meta.onderdelen[ond].codes[0].code
      setCode(firstCode)
      if (selectedGemeente) void fetchData(selectedGemeente.sid, ond, firstCode)
    }
  }

  const handleCodeChange = (c: string) => {
    setCode(c)
    setGebiedCode('')
    if (selectedGemeente && onderdeel) {
      void fetchData(selectedGemeente.sid, onderdeel, c, subSelect, '')
    }
  }

  const handleSubSelectChange = (v: string) => {
    setSubSelect(v)
    if (selectedGemeente && onderdeel && code) {
      void fetchData(selectedGemeente.sid, onderdeel, code, v, gebiedCode)
    }
  }

  const handleGebiedChange = (g: string) => {
    setGebiedCode(g)
    if (selectedGemeente && onderdeel && code) {
      void fetchData(selectedGemeente.sid, onderdeel, code, subSelect, g)
    }
  }

  const handleExport = async (allCodes: boolean) => {
    if (!selectedGemeente || !onderdeel) return
    try {
      const params = new URLSearchParams()
      if (!allCodes && code) params.append('code', code)
      if (mode === 'gebied' && beheer) params.append('beheer', 'true')
      const qs = params.toString() ? `?${params.toString()}` : ''
      // authToken.getToken() already returns the value prefixed with "Bearer ";
      // re-adding it breaks JWT decode in the API middleware.
      const token = authToken.getToken() || ''
      const response = await fetch(
        `${getUrl()}/gemeenten/${selectedGemeente.sid}/overige_data/${mode}/${onderdeel}/export${qs}`,
        { headers: { 'Authorization': token, 'accept-language': 'nl' } },
      )
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `overige_data_${mode}_${onderdeel}${!allCodes && code ? `_${code}` : ''}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Export mislukt')
    }
  }

  const handleDelete = async () => {
    if (!selectedGemeente || !onderdeel || !code) return
    setDeleting(true)
    try {
      const token = authToken.getToken() || ''
      await fetch(
        `${getUrl()}/gemeenten/${selectedGemeente.sid}/overige_data/${mode}/${onderdeel}/${encodeURIComponent(code)}`,
        { method: 'DELETE', headers: { Authorization: token } },
      )
      setDeleteOpen(false)
      setTableData(null)
      setCode('')
      await fetchMeta(selectedGemeente.sid, beheer)
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Verwijderen mislukt')
    } finally {
      setDeleting(false)
    }
  }

  const handleImportComplete = () => {
    setImportOpen(false)
    if (selectedGemeente) void fetchMeta(selectedGemeente.sid, beheer)
  }

  const currentOnderdeel = meta?.onderdelen[onderdeel]
  const codes = currentOnderdeel?.codes ?? []
  const showTreeview =
    mode === 'gebied' && ONDERDELEN_WITH_TREEVIEW.has(onderdeel) && tableData?.treeview
  const subSelectField = tableData?.sub_select ?? null
  const columns = useMemo(() => tableData?.columns ?? [], [tableData])

  // Search + sort + paginate
  const visibleData = useMemo(() => {
    if (!tableData?.data) return [] as Record<string, unknown>[]
    let rows = tableData.data
    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter((row) =>
        columns.some((c) => String(row[c] ?? '').toLowerCase().includes(q)),
      )
    }
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const av: unknown = a[sortField]
        const bv: unknown = b[sortField]
        if (av === bv) return 0
        if (av === null || av === undefined) return 1
        if (bv === null || bv === undefined) return -1
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortOrder === 'asc' ? av - bv : bv - av
        }
        return sortOrder === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av))
      })
    }
    return rows
  }, [tableData, search, sortField, sortOrder, columns])

  const pagedData = useMemo(
    () => visibleData.slice(page * pageSize, page * pageSize + pageSize),
    [visibleData, page, pageSize],
  )

  useEffect(() => {
    setPage(0)
  }, [search, sortField, sortOrder, pageSize])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title={title} />

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <GemeenteAutocomplete
              value={selectedGemeente}
              onChange={(gem) => void handleGemeenteChange(gem)}
            />
          </Grid>

          {meta && Object.keys(meta.onderdelen).length > 0 && (
            <>
              <Grid item xs={12} sm={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Onderdeel</InputLabel>
                  <Select
                    value={onderdeel}
                    label="Onderdeel"
                    onChange={(e) => handleOnderdeelChange(e.target.value)}
                  >
                    {Object.keys(meta.onderdelen).map((key) => (
                      <MenuItem key={key} value={key}>
                        {meta.onderdelen[key].label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {codes.length > 0 && (
                <Grid item xs={12} sm={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Code</InputLabel>
                    <Select
                      value={code}
                      label="Code"
                      onChange={(e) => handleCodeChange(e.target.value)}
                    >
                      {codes.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          {c.naam ? `${c.code} — ${c.naam}` : c.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {subSelectField && subSelectField.options.length > 0 && (
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sub-select</InputLabel>
                    <Select
                      value={subSelect || subSelectField.selected || ''}
                      label="Sub-select"
                      onChange={(e) => handleSubSelectChange(e.target.value)}
                    >
                      {subSelectField.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          {mode === 'gebied' && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={beheer}
                  onChange={(e) => void handleBeheerChange(e.target.checked)}
                />
              }
              label="Beheer (Gebiedsindeling)"
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => void handleExport(false)}
            disabled={!onderdeel || !code}
          >
            Export
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => void handleExport(true)}
            disabled={!onderdeel}
          >
            Export alle codes
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
            disabled={!selectedGemeente || !onderdeel}
          >
            Importeren
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}
            disabled={!code}
          >
            Verwijderen
          </Button>
        </Stack>
      </Paper>

      {meta?.info && selectedGemeente && (
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            Gemeente — {meta.info.naam || selectedGemeente.naam}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              <b>Code:</b> {meta.info.code || '—'}
            </Typography>
            <Typography variant="body2">
              <b>GI-code:</b> {meta.info.gi_code || '—'}
            </Typography>
            <Typography variant="body2">
              <b>GWB:</b> {meta.info.gwb ?? '—'}
            </Typography>
            <Typography variant="body2">
              <b>Profielcode:</b> {meta.info.profiel_code || '—'}
            </Typography>
          </Stack>
        </Paper>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {(loading || metaLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !metaLoading && tableData && (
        <Grid container spacing={2}>
          {showTreeview && (
            <Grid item xs={12} md={3}>
              <OverigeDataTreeview
                treeview={tableData.treeview ?? {}}
                selected={gebiedCode}
                onSelect={handleGebiedChange}
              />
            </Grid>
          )}
          <Grid item xs={12} md={showTreeview ? 9 : 12}>
            <Paper>
              <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Zoeken..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 240 }}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  {visibleData.length} van {tableData.total} rijen
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === col}
                            direction={sortField === col ? sortOrder : 'asc'}
                            onClick={() => handleSort(col)}
                          >
                            {col}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        {columns.map((col) => (
                          <TableCell key={col}>{formatCell(row[col])}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {tableData.totalen && (
                      <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid' } }}>
                        {columns.map((col) => (
                          <TableCell key={col}>
                            {col === columns[0]
                              ? 'Totalen'
                              : formatCell(tableData.totalen?.[col])}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={visibleData.length}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
              />
            </Paper>
          </Grid>
        </Grid>
      )}

      {!loading && !metaLoading && selectedGemeente && meta
        && Object.keys(meta.onderdelen).length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Geen overige data beschikbaar voor deze gemeente ({mode}
            {beheer ? ', beheer' : ''}).
          </Typography>
        </Paper>
      )}

      {!selectedGemeente && !loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Selecteer een gemeente om overige data te bekijken.
          </Typography>
        </Paper>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Data verwijderen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je de {currentOnderdeel?.label} data met code &quot;{code}&quot; wilt
            verwijderen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Annuleren
          </Button>
          <Button
            onClick={() => void handleDelete()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedGemeente && onderdeel && (
        <OverigeDataImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onComplete={handleImportComplete}
          gemeenteSid={selectedGemeente.sid}
          gemeenteNaam={selectedGemeente.naam}
          soort={mode}
          onderdeel={onderdeel}
          onderdeelLabel={currentOnderdeel?.label || onderdeel}
        />
      )}
    </Box>
  )
}

const formatCell = (val: unknown): string => {
  if (val === null || val === undefined) return ''
  if (typeof val === 'number') {
    // Years (1900-2200) render without thousand separator so '2.021' becomes '2021'.
    if (Number.isInteger(val) && val >= 1900 && val <= 2200) return String(val)
    return val.toLocaleString('nl-NL', { maximumFractionDigits: 4 })
  }
  return String(val)
}

export default OverigeDataView
